//src/services/bigQueryService.js
//
import { auth } from './firebase';
import { generarPassword } from '../utils/generarPassword';

const FUNCTIONS_URL = 'https://us-central1-app-newparalellum.cloudfunctions.net';

// Seguridad (Fase 2, jun 2026): TODAS las llamadas a Cloud Functions adjuntan
// el ID token de la sesión. Las CFs lo van exigiendo por tandas (middleware
// verificarAuthYRol); mientras una CF siga abierta, ignora el header y nada
// cambia. getIdToken() cachea internamente — costo ~0 por llamada.
async function fetchConToken(url, options = {}) {
  let token = '';
  try {
    if (auth.currentUser) token = await auth.currentUser.getIdToken();
  } catch (e) {
    // Sin token: la CF abierta responde igual; una protegida devolverá 401.
  }
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

// Facturación Forza (FACTURACION_FORZA_PLAN.md §7). Si la CF responde con un `codigo`
// (BORRADOR_EXISTE, YA_PROCESADA, FALTA_MES, HAY_PENDIENTES…) viaja en el error para que la
// pantalla ofrezca la salida que corresponde.
async function llamarFacturacionForza(nombre, body) {
  const response = await fetchConToken(`${FUNCTIONS_URL}/${nombre}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {})
  });
  const result = await response.json().catch(() => ({ success: false, error: `Error ${response.status} del servidor` }));
  if (!result.success) {
    const e = new Error(result.error || 'Error inesperado');
    e.codigo = result.codigo || null;
    e.datos = result;
    throw e;
  }
  return result;
}

// Productos 3D (docs/planes/PRODUCTOS_3D_PLAN.md §5): las 3 funciones devuelven { success, error } y, si sale bien, el
// resto de los campos en la raíz. `tope`: cuánto espera el navegador (un poco más que la función).
async function llamarProductos3D(nombre, body, tope) {
  const control = new AbortController();
  const reloj = setTimeout(() => control.abort(), tope);
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/${nombre}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
      signal: control.signal
    });
    const result = await response.json().catch(() => ({ success: false, error: `Error ${response.status} del servidor` }));
    if (!result.success) {
      const error = new Error(result.error || 'Error inesperado');
      // 'pedido_cambio' = otra persona cambió el pedido mientras estaba abierto: la pantalla lo vuelve a cargar.
      if (result.codigo) error.codigo = result.codigo;
      throw error;
    }
    return result;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Tardó demasiado y se canceló. Probá de nuevo.');
    throw error;
  } finally {
    clearTimeout(reloj);
  }
}

// Conciliación Bancaria (docs/planes/CONCILIACION_BANCARIA_PLAN.md §3): las 2 funciones devuelven { success, error } y,
// si sale bien, el resto en la raíz. El navegador espera un poco más que la función (120 s).
async function llamarConciliacion(nombre, body) {
  const control = new AbortController();
  const reloj = setTimeout(() => control.abort(), 130000);
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/${nombre}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
      signal: control.signal
    });
    const result = await response.json().catch(() => ({ success: false, error: `Error ${response.status} del servidor` }));
    if (!result.success) throw new Error(result.error || 'Error inesperado');
    return result;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Tardó demasiado y se canceló. Intentar de nuevo.');
    throw error;
  } finally {
    clearTimeout(reloj);
  }
}

// Lumi (docs/planes/ASISTENTE_IA_SISTEMA_PLAN.md): las dos funciones que contestan preguntas —`asistenteSistema`
// (Intermedio y Potente) y `asistenteBasico` (el Básico en su propia función, sin ningún camino a datos: segunda pared,
// 09/10/2026)— reciben y devuelven lo mismo.
async function llamarLumi(nombre, cuerpo) {
  const response = await fetchConToken(`${FUNCTIONS_URL}/${nombre}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo)
  });
  let result = null;
  try {
    result = await response.json();
  } catch (e) {
    // Sin JSON = la función no contestó (p. ej. 503 de Google mientras se despliega).
    const err = new Error('Lumi no responde en este momento. Probá de nuevo en un momento.');
    err.amigable = true;
    throw err;
  }
  return comprobarRespuestaLumi(result);
}

// La respuesta que se va escribiendo (Panel v2, tanda 5, 23/10/2026; plan de Lumi §13): el panel pide `transmitir: true`.
// Si la función la manda de a pedazos (renglones de JSON), `alParcial(texto)` recibe el texto ya limpio hasta ahí y al
// final vuelve la respuesta de siempre (el renglón `final`). Si contesta en JSON (la transmisión está apagada, o hubo un
// error antes de empezar), se lee igual que `llamarLumi`.
async function llamarLumiEnVivo(nombre, cuerpo, alParcial) {
  const response = await fetchConToken(`${FUNCTIONS_URL}/${nombre}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...cuerpo, transmitir: true })
  });
  const tipo = (response.headers && response.headers.get('content-type')) || '';
  if (!/ndjson/i.test(tipo) || !response.body || typeof response.body.getReader !== 'function') {
    let result = null;
    try {
      result = await response.json();
    } catch (e) {
      const err = new Error('Lumi no responde en este momento. Probá de nuevo en un momento.');
      err.amigable = true;
      throw err;
    }
    return comprobarRespuestaLumi(result);
  }
  const lector = response.body.getReader();
  const decodificador = new TextDecoder();
  let resto = '';
  let final = null;
  const procesar = (linea) => {
    if (!linea.trim()) return;
    let obj = null;
    try {
      obj = JSON.parse(linea);
    } catch (e) {
      return;
    }
    if (obj && obj.t === 'parcial') {
      if (typeof obj.texto === 'string' && typeof alParcial === 'function') alParcial(obj.texto);
    } else if (obj && (obj.t === 'final' || obj.t === 'error')) {
      final = obj;
    }
  };
  for (;;) {
    const { done, value } = await lector.read();
    if (done) break;
    resto += decodificador.decode(value, { stream: true });
    const lineas = resto.split('\n');
    resto = lineas.pop();
    lineas.forEach(procesar);
  }
  resto += decodificador.decode();
  if (resto) procesar(resto);
  if (!final) {
    const err = new Error('Lumi no terminó de contestar. Probá de nuevo en un momento.');
    err.amigable = true;
    throw err;
  }
  return comprobarRespuestaLumi(final);
}

function comprobarRespuestaLumi(result) {
  if (!result || !result.success) {
    const err = new Error((result && result.error) || 'No se pudo obtener respuesta de Lumi.');
    // El servidor ya redactó el aviso en español para el colaborador → el panel lo muestra tal cual.
    err.amigable = !!(result && result.amigable);
    // Mis conversaciones (22/10/2026): 'llena' (llegó al tope de preguntas) o 'no-encontrada', para que el panel reaccione.
    err.motivo = (result && typeof result.motivo === 'string' && result.motivo) || '';
    throw err;
  }
  return result;
}

export const BigQueryService = {
  consultarPedidos: async (filtros = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/consultarPedidos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filtros)
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.data;
    } catch (error) {
      console.error('Error consultando pedidos:', error);
      throw error;
    }
  },

  reporteGeneralGanancias: async (filtros) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/reporteGeneralGanancias`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filtros)
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error);
    }

    return result.data;
  } catch (error) {
    console.error('Error generando reporte de ganancias:', error);
    throw error;
  }
},

  auditarReporteGanancias: async (filtros) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/auditarReporteGanancias`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filtros)
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data;
    } catch (error) {
      console.error('Error auditando reporte de ganancias:', error);
      throw error;
    }
  },

  auditarReporteFinalGananciasNeto: async (params) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/auditarReporteFinalGananciasNeto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params)
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data;
    } catch (error) {
      console.error('Error auditando reporte final ganancias neto:', error);
      throw error;
    }
  },

  auditarProyeccionDiarios: async (params) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/auditarProyeccionDiarios`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params)
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data;
    } catch (error) {
      console.error('Error auditando proyección diarios:', error);
      throw error;
    }
  },

  // Fase A — Análisis exploratorio del lag de devoluciones
  // Devuelve estadísticas (lag promedio/mediano/percentiles, histograma, curva acumulada)
  // de los últimos N meses COMPLETOS. Default N=3.
  analizarLagDevoluciones: async (mesesAtras = 3, mesesExcluidos = []) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/analizarLagDevoluciones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mesesAtras, mesesExcluidos })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data;
    } catch (error) {
      console.error('Error analizando lag de devoluciones:', error);
      throw error;
    }
  },

  // Fase C.1 — Modelo predictivo persistente (Firestore: configuracion_modelo_predictivo/main)

  // Lee el modelo actual guardado (config + última calibración + historial de ejecuciones)
  obtenerModeloDevoluciones: async () => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerModeloDevoluciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error obteniendo modelo de devoluciones:', error);
      throw error;
    }
  },

  // Recalcula el modelo (usa la config persistente o overrides puntuales)
  recalcularModeloDevoluciones: async (opciones = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/recalcularModeloDevoluciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opciones)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error recalculando modelo de devoluciones:', error);
      throw error;
    }
  },

  // Fase C.3 — Calcular provisión por devoluciones esperadas (pedidos vivos)
  // Alimenta el escenario "Realista (con provisión)" del módulo Proyección Diarios.
  calcularProvisionDevoluciones: async ({ fechaInicio, fechaFin, filtros }) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/calcularProvisionDevoluciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fechaInicio, fechaFin, filtros })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error calculando provisión de devoluciones:', error);
      throw error;
    }
  },

  // Actualiza la config persistente (mesesExcluidos, mesesAtras, activo)
  actualizarConfigModeloDevoluciones: async (cambios) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarConfigModeloDevoluciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cambios)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error actualizando config modelo de devoluciones:', error);
      throw error;
    }
  },

  // Análisis General de Devoluciones (Tab 1) — calcula y persiste UN mes (botón "Calcular este mes")
  analizarDevolucionesMes: async (mes, triggeredBy = 'manual-ui') => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/analizarDevolucionesMes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mes, triggeredBy })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error analizando devoluciones del mes:', error);
      throw error;
    }
  },

  // Análisis General de Devoluciones (Tab 1) — lee el rango configurado + agrega el global
  obtenerAnalisisDevoluciones: async ({ mesesAtras, mesesExcluidos } = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerAnalisisDevoluciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mesesAtras, mesesExcluidos })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error obteniendo análisis general de devoluciones:', error);
      throw error;
    }
  },

reporteEnviadosForza: async (filtros) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/reporteEnviadosForza`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filtros)
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error generando reporte enviados Forza:', error);
    throw error;
  }
},

// Seguimiento de una guía vía la API oficial de Forza (modal de seguimiento).
// Devuelve { ok: true, tracking } o { ok: false, error } (mensaje apto para UI).
rastrearGuiaForza: async (numeroGuia) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaTracking`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ numeroGuia })
  });
  return await response.json();
},

// Aviso de agencia por WhatsApp (módulo Pedidos Agencia, botón "Enviar Mensaje").
// La CF confirma con Forza que el paquete SÍ está en la agencia antes de mandar nada.
// Respuesta: { ok, mensaje } o { ok:false, error, motivo? } — motivo 'SIN_CANAL'
// significa que la marca no tiene WhatsApp activo y hay que usar el flujo de wa.me.
enviarAvisoAgencia: async (pedidoId) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaEnviarAvisoAgencia`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pedidoId })
  });
  return await response.json();
},

// Estado real en Forza de un lote de guías (Seguimiento General de Pedidos).
// Devuelve { ok, estados: { FD123: { situacion, ultimoEstado, diasSinMovimiento,
// incidencias[], entregado } }, consultadas, desdeCache }.
// `pedidos` es un mapa opcional { GUIA: pedidoId } — deja la caché de estados sabiendo
// a qué pedido pertenece cada guía (el webhook no llega para todas).
forzaEstadoPedidos: async (guias, pedidos) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaEstadoPedidos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ guias, pedidos: pedidos || {} })
  });
  return await response.json();
},

// Envío MANUAL del aviso de incidencia al cliente (desde su conversación). Usa la misma
// plantilla y la misma cola que el aviso automático. Se bloquea si ya se le avisó.
forzaEnviarAvisoIncidencia: async (pedidoId) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaEnviarAvisoIncidencia`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pedidoId })
  });
  return await response.json();
},

// Catálogo de agencias (Express Centers) de Forza. La CF lo trae y lo GUARDA en
// `configuracion_forza/express_centers`; el selector lee ese doc directo (1 lectura).
forzaRefrescarAgencias: async () => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaRefrescarAgencias`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  return await response.json();
},

// Aviso de ENVÍO por WhatsApp (módulo Notificar Envío a Clientes).
// Respuesta: { ok, mensaje } o { ok:false, error, motivo? } — motivo 'SIN_CANAL'
// significa que la marca no tiene WhatsApp activo y hay que usar el flujo de wa.me.
enviarAvisoEnvio: async (pedidoId) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaEnviarAvisoEnvio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pedidoId })
  });
  return await response.json();
},

// Fase 2 API Forza — creación de guías por API (módulo Crear Guías Forza).
crearGuiasForza: async (pedidoIds, usuarioEmail, usuarioNombre) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaCrearGuias`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pedidoIds, usuarioEmail, usuarioNombre })
  });
  return await response.json();
},

obtenerBancosForza: async () => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaBancos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
  return await response.json();
},

subirHomologacionForza: async () => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaSubirHomologacion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
  return await response.json();
},

// Piloto automático de guías: encolar al pasar a POR ENVIAR + consolidar el día.
encolarGuiasForza: async (pedidoIds) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaEncolarGuias`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pedidoIds })
  });
  return await response.json();
},

consolidarDiaForza: async () => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaConsolidarDia`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
  return await response.json();
},

// Recupera de Forza las etiquetas faltantes de un día (guías creadas cuyo PDF no se
// pudo guardar) y rehace el PDF unido + manifiesto de ESE día. Se usa desde Guías y
// Manifiestos — ver FORZA_API.md. Sin `guias` recupera todas las faltantes del día.
// `extra` (28/09/2026, cuando Forza le niega los PDF al servidor):
// { etiquetasNavegador: [{ numeroGuia, pdfBase64 }] } manda los PDF que bajó el
// navegador, { rehacerDocumentos: false } deja los documentos para el final y
// { soloRehacerDocumentos: true } solo rehace el PDF del día y el manifiesto.
// Turno compartido de la recuperación de etiquetas (05/10/2026): { accion: 'tomar' | 'latido' |
// 'soltar', … }. Lo atiende la misma función (functions/forza/turnoEtiquetas.js).
turnoEtiquetasForza: async (turno) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaRecuperarEtiquetas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ turno })
  });
  return await response.json();
},

recuperarEtiquetasForza: async (solicitudId, guias = null, extra = {}) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaRecuperarEtiquetas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ solicitudId, ...(guias ? { guias } : {}), ...extra })
  });
  return await response.json();
},

etiquetasLoteForza: async (storagePaths) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaEtiquetasLote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storagePaths })
  });
  return await response.json();
},

guardarManifiestoForza: async ({ pdfBase64, nombreArchivo, fechaEnvio, empresaNombre, solicitudId }) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/forzaGuardarManifiesto`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pdfBase64, nombreArchivo, fechaEnvio, empresaNombre, solicitudId })
  });
  return await response.json();
},

  obtenerPedidosParaNotificar: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerPedidosParaNotificar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo pedidos para notificar:', error);
    throw error;
  }
},

obtenerPedidosAgencia: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerPedidosAgencia`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo pedidos de agencia:', error);
    throw error;
  }
},

obtenerPedidosCongelados: async (filtroTipo = 'todos') => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerPedidosCongelados`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filtroTipo })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo pedidos congelados:', error);
    throw error;
  }
},

generarReportePedidos: async (filtros) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/generarReportePedidos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filtros)
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error generando reporte pedidos:', error);
    throw error;
  }
},

kardexProductos: async (params) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/kardexProductos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result.data;
  } catch (error) {
    console.error('Error en kardexProductos:', error);
    throw error;
  }
},

// Auditoría / Reconciliación de Inventario. params: { modo:'general'|'producto', productoIds?, bodegaId?, estados?, soloDescuadres?, umbralDescuadre? }
// Alertas de Reposición: estado de los productos vigilados (stock vs límite).
// El servidor cachea 15 min; `forzar` recalcula (lo usa el módulo, no el widget).
evaluarAlertasReposicion: async (forzar = false) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/evaluarAlertasReposicion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ forzar })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return { items: result.items || [], bajos: result.bajos || 0, actualizadoMs: result.actualizadoMs || 0 };
  } catch (error) {
    console.error('Error en evaluarAlertasReposicion:', error);
    throw error;
  }
},

auditarInventario: async (params) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/auditarInventario`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result.data;
  } catch (error) {
    console.error('Error en auditarInventario:', error);
    throw error;
  }
},

obtenerSolicitudesAnulacion: async (fechaInicio, fechaFin, estado) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerSolicitudesAnulacion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fechaInicio, fechaFin, estado })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo solicitudes anulación:', error);
    throw error;
  }
},

obtenerSolicitudesAnulacionConEstadoActual: async (fechaInicio, fechaFin, estado) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerSolicitudesAnulacionConEstadoActual`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fechaInicio, fechaFin, estado })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo solicitudes anulación con estado actual:', error);
    throw error;
  }
},

obtenerSolicitudesAnulacionFiltradas: async (filtros) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerSolicitudesAnulacionFiltradas`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filtros)
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo solicitudes filtradas:', error);
    throw error;
  }
},

obtenerSolicitudesAnulacionFiltradasConEstadoActual: async ({ fechaInicio, fechaFin, estado, origen, tipoRed }) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerSolicitudesAnulacionFiltradasConEstadoActual`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fechaInicio, fechaFin, estado, origen, tipoRed })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo solicitudes anulación filtradas con estado actual:', error);
    throw error;
  }
},

obtenerAutorizacionesPago: async (email, fechaInicio, fechaFin, estado) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerAutorizacionesPago`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, fechaInicio, fechaFin, estado })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo autorizaciones:', error);
    throw error;
  }
},

obtenerSolicitudesPendientesPago: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerSolicitudesPendientesPago`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo solicitudes pendientes:', error);
    throw error;
  }
},

obtenerHistorialAutorizaciones: async (fechaInicio, fechaFin, estado) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerHistorialAutorizaciones`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fechaInicio, fechaFin, estado })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo historial:', error);
    throw error;
  }
},


reporteNotificacionesEnviadas: async (fechaInicio, fechaFin) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/reporteNotificacionesEnviadas`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fechaInicio, fechaFin })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return {
      pedidos: result.data,
      estadisticas: result.estadisticas
    };
  } catch (error) {
    console.error('Error generando reporte:', error);
    throw error;
  }
},

// Restablecer contraseña de usuario (Admin)
  restablecerPasswordAdmin: async (userId, email, nombre) => {
    try {
      // La contraseña se genera ACÁ con crypto seguro y se envía a la CF
      // (que exige sesión de admin). El servidor no la genera ni la devuelve.
      // La generación vive en `utils/generarPassword` porque el formulario de
      // usuario nuevo usa exactamente la misma (una sola copia, un solo criterio).
      const nuevaPassword = generarPassword();

      const idToken = await auth.currentUser.getIdToken();
      const response = await fetchConToken(`${FUNCTIONS_URL}/restablecerPasswordAdmin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ userId, email, nombre, nuevaPassword })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Error al restablecer contraseña');
      }

      return {
        mensaje: result.mensaje,
        password: nuevaPassword,
        email: result.email
      };
    } catch (error) {
      console.error('Error restableciendo contraseña:', error);
      throw error;
    }
  },

// Inactivar/reactivar usuario en Firebase Auth (Admin).
// Inactivar deshabilita la cuenta y revoca sus tokens → la sesión abierta se cierra.
  actualizarEstadoUsuarioAdmin: async (userId, activo) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarEstadoUsuarioAdmin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ userId, activo })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Error al actualizar el estado del usuario');
      }

      return result;
    } catch (error) {
      console.error('Error actualizando estado del usuario:', error);
      throw error;
    }
  },


obtenerEstadisticasPedidosSAC: async (anio, mes, emails = null, nombres = null, forzarRecalculo = false) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerEstadisticasPedidosSAC`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anio, mes, emails, nombres, forzarRecalculo })
    });

    const data = await response.json();
    if (!data.success) throw new Error(data.error);

    return {
      ...data.data,
      fromCache: data.fromCache || false
    };
  } catch (error) {
    console.error('Error obteniendo estadísticas de pedidos SAC:', error);
    throw error;
  }
},


  controlFinancieroCapital: async (año, mes) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/controlFinancieroCapital`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ año, mes })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.data;
    } catch (error) {
      console.error('Error en control financiero:', error);
      throw error;
    }
  },

  cuentasPedidosMensajero: async (mensajero) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/cuentasPedidosMensajero`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mensajero })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error en cuentas pedidos mensajero:', error);
    throw error;
  }
},

buscarPedidosDevolucion: async (filtros) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/buscarPedidosDevolucion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filtros)
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error buscando pedidos devolución:', error);
    throw error;
  }
},

buscarPedidosPorGuias: async (guias) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/buscarPedidosPorGuias`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ guias })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error buscando pedidos por guías:', error);
    throw error;
  }
},


consultarMetasSAC: async (año, mes, usuariosEmails = null) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/consultarMetasSAC`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ año, mes, usuariosEmails })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error consultando metas SAC:', error);
    throw error;
  }
},

obtenerProyeccionesMetas: async (año, mes) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerProyeccionesMetas`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ año, mes })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo proyecciones:', error);
    throw error;
  }
},

// Las metas SAC del mes SIN el bono (Metas SAC Operativo, 06/10/2026): correo, nombre y los rangos con su
// número, mínimo y máximo. La tarifa no llega al navegador.
obtenerMetasSACSinBono: async (año, mes) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerMetasSACSinBono`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ año, mes })
    });

    const result = await response.json();
    if (!result.success) throw new Error(result.error);

    return result.data;
  } catch (error) {
    console.error('Error obteniendo las metas sin bono:', error);
    throw error;
  }
},

// Nueva función optimizada para obtener datos de metas con caché
obtenerDatosMetasUsuario: async (año, mes, email, nombre = null, forzarRecalculo = false) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerDatosMetasUsuario`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ año, mes, email, nombre, forzarRecalculo })
    });

    const result = await response.json();
    if (!result.success) throw new Error(result.error);

    return {
      ...result.data,
      fromCache: result.fromCache || false
    };
  } catch (error) {
    console.error('Error obteniendo datos de metas:', error);
    throw error;
  }
},



  controlPedidosMensajero: async (mensajero, año, mes, estado) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/controlPedidosMensajero`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mensajero, año, mes, estado })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error en control pedidos mensajero:', error);
    throw error;
  }
},

obtenerGastosProyecciones: async (año, mes) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerGastosProyecciones`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ año, mes })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo gastos proyecciones:', error);
    throw error;
  }
},

obtenerPromediosGastos: async (año, mes, mesesAtras = 4) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerPromediosGastos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ año, mes, mesesAtras })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo promedios gastos:', error);
    throw error;
  }
},

obtenerGastosPublicitariosPorEmpresa: async (año, mes) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerGastosPublicitariosPorEmpresa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ año, mes })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo gastos por empresa:', error);
    throw error;
  }
},

  // NOTA: reporteFinalGananciasNeto y reporteFinalGananciasNetoOptimizado fueron removidos
  // del service en mayo 2026 — no se usan en ningún componente del frontend.
  // Las Cloud Functions con esos nombres siguen existiendo en GCP pero quedan idle.

  indicesCapital: async (año, mes) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/indicesCapital`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ año, mes })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error en índices capital:', error);
    throw error;
  }
},

  obtenerDevolucionesSinVerificar: async (filtros) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerDevolucionesSinVerificar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filtros)
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.data;
    } catch (error) {
      console.error('Error obteniendo devoluciones sin verificar:', error);
      throw error;
    }
  },

  obtenerPeriodosDisponiblesDevoluciones: async () => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerPeriodosDisponiblesDevoluciones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.data;
    } catch (error) {
      console.error('Error obteniendo períodos disponibles:', error);
      return {};
    }
  },

  obtenerPedidosSimple: async (filtros) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerPedidosSimple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filtros)
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.data;
    } catch (error) {
      console.error('Error obteniendo pedidos simple:', error);
      throw error;
    }
  },

  obtenerUsuariosConPedidos: async () => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerUsuariosConPedidos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.data;
    } catch (error) {
      console.error('Error obteniendo usuarios con pedidos:', error);
      throw error;
    }
  },

  // Consultar devoluciones por fecha para ingreso
  consultarDevolucionesPorFechaIngreso: async (fecha) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/consultarDevolucionesPorFechaIngreso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fecha })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return {
        productos: result.productos || [],
        totalDevoluciones: result.totalDevoluciones || 0
      };
    } catch (error) {
      console.error('Error consultando devoluciones para ingreso:', error);
      throw error;
    }
  },

  // Crear usuario (Cloud Function callable)
crearUsuarioAdmin: async (email, password, nombre, rol, activo) => {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const crearUsuarioFn = httpsCallable(functions, 'crearUsuarioAdmin');
    
    const resultado = await crearUsuarioFn({
      email,
      password,
      nombre,
      rol,
      activo
    });
    
    return resultado.data;
  } catch (error) {
    console.error('Error creando usuario:', error);
    throw error;
  }
},

consultarPedidosAnuladosSAC: async (fechaEnvio, tipoRed = null, fechaEnvioDesde = null) => {
  try {
    // Dos modos: exacto (fechaEnvio) o rango hacia adelante (fechaEnvioDesde, solo POR ENVIAR/ENVIADO).
    console.log('📡 Consultando pedidos', fechaEnvioDesde ? `desde: ${fechaEnvioDesde}` : `para fecha: ${fechaEnvio}`, 'Tipo:', tipoRed || 'todos');

    const response = await fetchConToken(`${FUNCTIONS_URL}/consultarPedidosAnuladosSAC`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fechaEnvio, tipoRed, fechaEnvioDesde })
    });

    console.log('📥 Respuesta HTTP status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Error HTTP:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('📦 Resultado:', result);
    
    if (!result.success) {
      console.error('❌ Error en resultado:', result.error || result.details);
      throw new Error(result.error || result.details || 'Error en la consulta');
    }

    console.log('✅ Pedidos obtenidos:', result.pedidos?.length || 0);
    return result.pedidos || [];
  } catch (error) {
    console.error('💥 Error completo consultando pedidos anulados SAC:', error);
    throw error;
  }
},

  contarProductosRopaDia: async (fecha, esCapital) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/contarProductosRopaDia`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fecha, esCapital })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return {
      cantidad: result.cantidad,
      fecha: result.fecha,
      esCapital: result.esCapital
    };
  } catch (error) {
    console.error('Error contando productos de ropa:', error);
    throw error;
  }
},

// Obtener estadísticas de pedidos del día
obtenerEstadisticasPedidosDia: async (fecha, forzarRecalculo = false) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerEstadisticasPedidosDia`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fecha, forzarRecalculo })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return {
      totalPedidos: result.totalPedidos,
      pedidosValidos: result.pedidosValidos,
      estadisticasEstados: result.estadisticasEstados,
      totalVentas: result.totalVentas,
      totalVentasSinEnvio: result.totalVentasSinEnvio,
      desgloseCapitalForza: result.desgloseCapitalForza,
      enviosCapitalForza: result.enviosCapitalForza,
      fromCache: result.fromCache || false,
      ultimaActualizacion: result.ultimaActualizacion || null
    };
  } catch (error) {
    console.error('Error obteniendo estadísticas pedidos día:', error);
    throw error;
  }
},

  // ===== CONTROL DE IMPRESIONES (BigQuery + cache) =====
  // Necesidades de impresión por producto. Cache para filtros típicos (hoy/manana/3dias/semana).
  // filtroFecha: 'hoy' | 'manana' | '3dias' | 'semana' | 'personalizado'
  // Para 'personalizado' siempre calcula (sin cache).
  obtenerNecesidadesImpresion: async (fechaDesde, fechaHasta, filtroFecha = 'personalizado', forzarRecalculo = false) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerNecesidadesImpresion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fechaDesde, fechaHasta, filtroFecha, forzarRecalculo })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return {
        resultados: result.resultados || [],
        fromCache: result.fromCache || false,
        ultimaActualizacion: result.ultimaActualizacion || null
      };
    } catch (error) {
      console.error('Error obteniendo necesidades de impresión:', error);
      throw error;
    }
  },

  // (obtenerStockImpresiones eliminada — el stock ahora es un counter Firestore `stock_impresiones`
  // mantenido por triggers/eventos del cliente. Se lee directo de Firestore, instantáneo.)

  // Consultar devoluciones por fecha
  consultarDevolucionesPorFecha: async (fecha) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/consultarDevolucionesPorFecha`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fecha })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.devoluciones || [];
    } catch (error) {
      console.error('Error consultando devoluciones:', error);
      throw error;
    }
  },
  

// Consultar pedidos para cambio de estado
  consultarPedidosCambioEstado: async (fechaInicio, fechaFin, estados, numeroPedido) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/consultarPedidosCambioEstado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fechaInicio, fechaFin, estados, numeroPedido })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.pedidos || [];
    } catch (error) {
      console.error('Error consultando pedidos cambio estado:', error);
      throw error;

       }
  },   

// ========================================
// FUNCIONES DE META ADS
// ========================================

// Obtener métricas de TODAS las cuentas
getAllMetaAdsInsights: async (datePreset = 'last_30d', fields = 'spend,impressions,clicks,ctr,cpc,cpm,reach,actions,action_values,cost_per_action_type,website_purchase_roas', fechaInicio = null, fechaFin = null) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/getAllMetaAdsInsights`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ datePreset, fields, fechaInicio, fechaFin })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return {
      accounts: result.accounts,
      totals: result.totals
    };
  } catch (error) {
    console.error('Error obteniendo todos los insights de Meta Ads:', error);
    throw error;
  }
},

// Sincronizar gastos Meta Ads de un mes completo
sincronizarGastosMetaAdsMes: async (periodo, usuario) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/sincronizarGastosMetaAdsMes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ periodo, usuario })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error sincronizando gastos Meta:', error);
    throw error;
  }
},

// ────────────────────────────────────────────────────────────
// FORZA — envío automático del Excel + cancelar espera
// ────────────────────────────────────────────────────────────
enviarExcelAForza: async (payload) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/enviarExcelAForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error enviando Excel a FORZA:', error);
    throw error;
  }
},

// Encola una tanda completa de envíos en el servidor (un correo por minuto desde el cron).
// payload: { envios: [{ empresa, referenciaForza, excelBase64, nombreArchivo, pedidoIds, referenciasInternas, fechaEnvio }], usuarioEmail }
encolarEnviosForza: async (payload) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/encolarEnviosForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error encolando envíos a FORZA:', error);
    throw error;
  }
},

cancelarEsperaForza: async (solicitudId, usuarioEmail) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/cancelarEsperaForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ solicitudId, usuarioEmail })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error cancelando espera FORZA:', error);
    throw error;
  }
},

forzarChequeoGuiasForza: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/forzarChequeoGuiasForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error forzando chequeo FORZA:', error);
    throw error;
  }
},

/**
 * Lee los correos "Informe de Depósitos COD" de FORZA (gerencia@) y procesa las entregas:
 * marca pedidos como ENTREGADO, registra comisión/envío real y guarda el .xls en Drive.
 * payload: { usuarioEmail }
 * Respuesta: { success: true, resumen: { correosEncontrados, procesados:[...], yaProcesados, sinAdjunto, paraRevisar, errores:[] } }
 */
procesarEntregasForza: async (usuarioEmail, dryRun = false) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/forzarProcesarEntregasForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ usuarioEmail: usuarioEmail || '', dryRun: !!dryRun })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error procesando entregas FORZA:', error);
    throw error;
  }
},

// Carga MANUAL del Informe de Depósitos de FORZA (el que mandan por WhatsApp).
// El parseo, la actualización de pedidos y el registro de liquidaciones pasan en el
// servidor: `liquidaciones_forza` no se puede escribir desde el navegador, y el informe
// mensual trae miles de guías. Con dryRun=true solo devuelve la vista previa (no escribe).
// tipo: 'cod' (Informe de Depósitos, default) | 'estandar' (Excel del servicio estándar, sin cobro).
procesarInformeForzaManual: async (excelBase64, nombreArchivo, dryRun = true, tipo = 'cod') => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/procesarInformeForzaManual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ excelBase64, nombreArchivo, dryRun: !!dryRun, tipo })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error procesando informe FORZA manual:', error);
    throw error;
  }
},

// ── Facturación Forza (factura mensual) ──
forzaFacturaAnalizar: (payload) => llamarFacturacionForza('forzaFacturaAnalizar', payload),
forzaFacturaRastrear: (payload) => llamarFacturacionForza('forzaFacturaRastrear', payload),
forzaFacturaGuardarRevision: (payload) => llamarFacturacionForza('forzaFacturaGuardarRevision', payload),
forzaFacturaAplicar: (payload) => llamarFacturacionForza('forzaFacturaAplicar', payload),
forzaFacturaEliminarBorrador: (payload) => llamarFacturacionForza('forzaFacturaEliminarBorrador', payload),

limpiarHistoricoForza: async (payload) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/limpiarHistoricoForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error limpiando histórico FORZA:', error);
    throw error;
  }
},

/**
 * Sube un Excel a Google Drive en la estructura ALFA/FORZA/GUIAS Y MANIFIESTOS/.../EMPRESA/.
 *
 * payload: {
 *   fechaEnvio: "18-05-2026",
 *   empresa: "CLOUDY",
 *   nombreArchivo: "ENVIOS_CLOUDY_180526.xls",
 *   excelBase64: "...",
 *   modoSiExiste: "preguntar" | "sobrescribir" | "agregarHora"  (default "preguntar")
 * }
 *
 * Respuesta:
 *   - Si subió: { success: true, ok: true, fileId, webViewLink, nombreFinal, sobrescrito? }
 *   - Si existe y modo='preguntar': { success: true, existe: true, archivoExistente, nombreSugerido }
 */
subirExcelADriveManual: async (payload) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/subirExcelADriveManual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error subiendo Excel a Drive:', error);
    throw error;
  }
},

colocarGuiasManualForza: async (solicitudId, dryRun = false) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/colocarGuiasManualForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ solicitudId, dryRun })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error colocando guías FORZA manual:', error);
    throw error;
  }
},

colocarGuiasDesdePDF: async (pdfs, dryRun = false, usuarioEmail = '') => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/colocarGuiasDesdePDF`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pdfs, dryRun, usuarioEmail })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error colocando guías desde PDF:', error);
    throw error;
  }
},

cancelarYLimpiarSolicitudForza: async (solicitudId, usuarioEmail) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/cancelarYLimpiarSolicitudForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ solicitudId, usuarioEmail })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error cancelando+limpiando solicitud FORZA:', error);
    throw error;
  }
},

eliminarSolicitudForza: async (solicitudId, usuarioEmail) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/eliminarSolicitudForza`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ solicitudId, usuarioEmail })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error eliminando solicitud FORZA:', error);
    throw error;
  }
},

// Obtener auditoría de cuentas Meta (todas las cuentas del business + gasto 30d).
// Devuelve { cuentas, aviso }: `aviso` = por qué Meta no entregó la lista (token vencido o inválido, freno…); cada
// cuenta trae gasto30d null + errorGasto si su gasto no se pudo leer (no es Q0) (05/10/2026).
obtenerCuentasMetaAuditoria: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerCuentasMetaAuditoria`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return { cuentas: result.cuentas || [], aviso: result.aviso || null };
  } catch (error) {
    console.error('Error obteniendo auditoría Meta:', error);
    throw error;
  }
},

// Obtener campañas de una cuenta
getMetaAdsCampaigns: async (accountId) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/getMetaAdsCampaigns`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ accountId })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error);
    }
    
    return result.data;
  } catch (error) {
    console.error('Error obteniendo campañas de Meta Ads:', error);
    throw error;
  }
},

// Stock disponible (con reservas descontadas, igual que el que va a Shopify) de
// productos puntuales — semáforo de la pestaña "Vigilancia de stock" del Ads Dashboard.
obtenerStockDisponibleVigilancia: async (productoIds) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerStockDisponibleVigilancia`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productoIds })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result.stock; // { [productoId]: { disponible, nombre, codigo } }
  } catch (error) {
    console.error('Error obteniendo stock de vigilancia:', error);
    throw error;
  }
},

// Campañas activas (cuenta activa + entrega real + cuentas omitidas, ya filtradas en el
// backend) para la pestaña "Vigilancia de stock". Misma fuente que el cron.
listarCampanasActivasVigilancia: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/listarCampanasActivasVigilancia`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    // campanas: [{ plataforma, campaignId, campaignNombre, cuentaId, cuentaNombre }]
    // metaError/tiktokError: si una plataforma NO se pudo consultar (ej. token Meta
    // invalidado), acá viene el motivo — lista vacía por error ≠ "no hay campañas".
    // cuentasConError: [{ plataforma, cuentaId, cuentaNombre, error }] — cuentas sueltas que no
    // contestaron aunque la plataforma sí (sus pautas no vienen en la lista, pero NO están pausadas).
    return {
      campanas: result.campanas || [],
      metaError: result.metaError || null,
      tiktokError: result.tiktokError || null,
      cuentasConError: result.cuentasConError || []
    };
  } catch (error) {
    console.error('Error listando campañas de vigilancia:', error);
    throw error;
  }
},


// Estado del token Meta (días restantes, fuente, etc) — NO devuelve el token mismo
obtenerEstadoTokenMeta: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerEstadoTokenMeta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result.data;
  } catch (error) {
    console.error('Error obteniendo estado token Meta:', error);
    throw error;
  }
},

// Actualizar el token Meta manualmente (admin pega el token nuevo)
actualizarTokenMeta: async (accessToken, appId, appSecret, usuario) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarTokenMeta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken, appId, appSecret, usuario })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error actualizando token Meta:', error);
    throw error;
  }
},

// Renovar el token Meta automáticamente (intercambio fb_exchange_token)
renovarTokenMetaAuto: async (usuario) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/renovarTokenMetaAuto`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ usuario })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error renovando token Meta automáticamente:', error);
    throw error;
  }
},

// Verificar si el usuario debe ver alerta de token Meta por vencer (≤7 días)
// La CF también crea la notificación en Firestore con dedup por día.
verificarAlertaTokenMeta: async (userEmail) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/verificarAlertaTokenMeta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userEmail })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error verificando alerta token Meta:', error);
    // En caso de error, no romper la app — solo no mostrar alerta
    return { mostrarAlerta: false };
  }
},

// ========================================
// ASISTENTE IA (Lumi)
// ========================================

// El método del asistente viejo (función `asistenteIA`, Groq) se retiró el 09/10/2026 (plan de Lumi §14).
// Lumi, el Asistente IA del Sistema (01/10/2026, docs/planes/ASISTENTE_IA_SISTEMA_PLAN.md).
// { accion: 'preguntar', pregunta, historial, ruta } → { respuesta, modulos, nivel, consultaId }
// { accion: 'calificar', consultaId, valor: 1 | -1 } → { success }
// Lo que Lumi sabe lo arma el servidor con los permisos de la persona: acá no se manda contexto.
asistenteSistema: (cuerpo) => llamarLumi('asistenteSistema', cuerpo),
// El Básico en su propia función (segunda pared, 09/10/2026): mismo cuerpo y misma respuesta que asistenteSistema.
asistenteBasico: (cuerpo) => llamarLumi('asistenteBasico', cuerpo),
// Las mismas preguntas, con la respuesta que se va escribiendo (tanda 5): `alParcial(texto)` mientras llega.
asistenteSistemaEnVivo: (cuerpo, alParcial) => llamarLumiEnVivo('asistenteSistema', cuerpo, alParcial),
asistenteBasicoEnVivo: (cuerpo, alParcial) => llamarLumiEnVivo('asistenteBasico', cuerpo, alParcial),

// Módulo de administración de Lumi (/lumi): { accion: 'resumen', desde, hasta } | { accion: 'detalle', id }.
// Solo lee. El servidor deja entrar a un admin o a quien tenga el módulo `lumi` en Permisos (403 si no).
asistenteSistemaAdmin: async (cuerpo) => {
  const response = await fetchConToken(`${FUNCTIONS_URL}/asistenteSistemaAdmin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo)
  });
  let result = null;
  try {
    result = await response.json();
  } catch (e) {
    const err = new Error('El servidor de Lumi no responde en este momento. Probá de nuevo en un momento.');
    err.amigable = true;
    throw err;
  }
  if (!result || !result.success) {
    const err = new Error((result && result.error) || 'No se pudo cargar la información de Lumi.');
    err.amigable = !!(result && result.amigable);
    err.sinPermiso = response.status === 403;
    throw err;
  }
  return result;
},

// ========================================
// FUNCIONES DE TIKTOK ADS
// ========================================

// Intercambiar auth_code por access_token + advertiser_ids (OAuth)
tiktokIntercambiarCodigoAuth: async (authCode, appId, appSecret, usuario) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/tiktokIntercambiarCodigoAuth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authCode, appId, appSecret, usuario })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error en OAuth TikTok:', error);
    throw error;
  }
},

// Insights de todas las cuentas TikTok (paralelo a getAllMetaAdsInsights)
getAllTikTokAdsInsights: async (datePreset = 'last_30d', fechaInicio = null, fechaFin = null) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/getAllTikTokAdsInsights`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ datePreset, fechaInicio, fechaFin })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error obteniendo insights TikTok:', error);
    throw error;
  }
},

// Audiencias reales de TikTok (edad × género, ubicación, sistema) de las cuentas con gasto, en el MISMO rango del
// reporte (el `rango` que devuelve getAllTikTokAdsInsights). CF 2ª gen getTikTokAdsAudiencias.
getTikTokAdsAudiencias: async (desde, hasta, cuentas) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/getTikTokAdsAudiencias`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ desde, hasta, cuentas })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error obteniendo audiencias TikTok:', error);
    throw error;
  }
},

// Despierta funciones que se van a usar en unos segundos (30/09/2026), para que su arranque en frío (4-9 s: cada una
// carga todo functions/index.js) pase mientras la persona espera otra cosa, por ejemplo el reporte del Ads Dashboard.
// Sin sesión ni datos (getAdsTendenciaDiaria contesta 204 a `?despertar=1`; las de 1ª gen, "sin sesión"). Nunca falla
// hacia afuera: si no llega, la función arranca cuando se la use, como siempre.
despertarFunciones: (nombres) => {
  (nombres || []).forEach((nombre) => {
    fetch(`${FUNCTIONS_URL}/${nombre}?despertar=1`, { method: 'GET', mode: 'no-cors' }).catch(() => {});
  });
},

// Tendencia REAL día por día (gasto, impresiones, clics) de las cuentas con gasto del reporte, con los mismos N días
// justo antes para comparar — pestaña Tendencias de Meta y de TikTok. `plataforma`: 'meta' | 'tiktok';
// `cuentas`: [{ id, desde, hasta }] con el rango del reporte de cada cuenta. CF 2ª gen getAdsTendenciaDiaria.
getAdsTendenciaDiaria: async (plataforma, cuentas) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/getAdsTendenciaDiaria`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plataforma, cuentas })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error obteniendo la tendencia día por día:', error);
    throw error;
  }
},

// Campañas de un advertiser TikTok
getTikTokAdsCampaigns: async (accountId) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/getTikTokAdsCampaigns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result.data;
  } catch (error) {
    console.error('Error obteniendo campañas TikTok:', error);
    throw error;
  }
},


// Lista de advertisers autorizados
getTikTokAdsAccounts: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/getTikTokAdsAccounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result.data;
  } catch (error) {
    console.error('Error obteniendo cuentas TikTok:', error);
    throw error;
  }
},

// Auditoría: lista completa de advertisers + gasto últimos 30d.
// Devuelve { cuentas, aviso }: `aviso` = por qué TikTok no entregó la lista (sin token, token vencido, freno…); cada
// cuenta trae spend_last_30d null + errorGasto si su gasto no se pudo leer (no es Q0) (06/10/2026).
obtenerCuentasTikTokAuditoria: async () => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerCuentasTikTokAuditoria`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return { cuentas: result.data || [], aviso: result.aviso || null };
  } catch (error) {
    console.error('Error en auditoría TikTok:', error);
    throw error;
  }
},

// Refresca la lista de advertisers autorizados (config_tiktok_sync/main.advertiserIds)
// con lo que el token tiene autorizado HOY. Devuelve {total, nuevas, nombresNuevas, removidas}.
refrescarAdvertisersTikTok: async (usuario) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/refrescarAdvertisersTikTok`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ usuario: usuario || 'desconocido' })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error refrescando advertisers TikTok:', error);
    throw error;
  }
},

// Sincronizar un mes completo desde TikTok
sincronizarGastosTikTokMes: async (periodo, usuario) => {
  try {
    const response = await fetchConToken(`${FUNCTIONS_URL}/sincronizarGastosTikTokMes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ periodo, usuario })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error sincronizando gastos TikTok:', error);
    throw error;
  }
},

  // ========================================
  // FUNCIONES DE SHOPIFY
  // ========================================

  // Obtener órdenes de Shopify
  obtenerOrdenesShopify: async (filtros = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerOrdenesShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filtros)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return { data: result.data, hayMas: result.hayMas || false };
    } catch (error) {
      console.error('Error obteniendo órdenes Shopify:', error);
      throw error;
    }
  },

  // Obtener tiendas Shopify configuradas
  obtenerTiendasShopify: async () => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerTiendasShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error obteniendo tiendas Shopify:', error);
      throw error;
    }
  },

  // Actualizar seguimiento de orden (llamadas, whatsapp, correo)
  actualizarSeguimientoOrden: async (ordenId, campo, datos) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarSeguimientoOrden`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordenId, campo, datos })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error actualizando seguimiento:', error);
      throw error;
    }
  },

  // Actualizar nota del cliente en orden
  actualizarNotaOrden: async (ordenId, nota, usuario, usuarioNombre) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarNotaOrden`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordenId, nota, usuario, usuarioNombre })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error actualizando nota:', error);
      throw error;
    }
  },

  // Actualizar observaciones internas de orden (solo local)
  actualizarObservacionesOrden: async (ordenId, observaciones, usuario, usuarioNombre) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarObservacionesOrden`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordenId, observaciones, usuario, usuarioNombre })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error actualizando observaciones:', error);
      throw error;
    }
  },

  // Cancelar orden de Shopify
  cancelarOrdenShopify: async (ordenId, motivo, usuario, usuarioNombre) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/cancelarOrdenShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordenId, motivo, usuario, usuarioNombre })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error cancelando orden:', error);
      throw error;
    }
  },

  // Des-cancelar orden de Shopify (revertir cancelación)
  descancelarOrdenShopify: async (ordenId, usuarioId, usuarioNombre) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/descancelarOrdenShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordenId, usuarioId, usuarioNombre })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error des-cancelando orden:', error);
      throw error;
    }
  },

  // Procesar orden de Shopify (congelar/crear pedido)
  procesarOrdenShopify: async (ordenId, usuarioId, usuarioNombre) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/procesarOrdenShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordenId, usuarioId, usuarioNombre })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error procesando orden:', error);
      throw error;
    }
  },


  reportePedidosPorUbicacion: async (filtros) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/reportePedidosPorUbicacion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filtros)
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data;
    } catch (error) {
      console.error('Error generando reporte de ubicaciones:', error);
      throw error;
    }
  },

  // ========================================
  // SINCRONIZACIÓN DE INVENTARIO A SHOPIFY
  // ========================================

  // Obtener ubicaciones de Shopify (locations)
  obtenerUbicacionesShopify: async (tiendaId) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerUbicacionesShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error obteniendo ubicaciones de Shopify:', error);
      throw error;
    }
  },

  // Sincronizar inventario a Shopify (completo)
  sincronizarInventarioShopify: async (tiendaId, locationId, usuario, usuarioNombre, soloSimular = false) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/sincronizarInventarioShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, locationId, usuario, usuarioNombre, soloSimular })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error sincronizando inventario a Shopify:', error);
      throw error;
    }
  },

  // Sincronizar productos afectados por cambios de inventario (pedidos manuales, ingresos, traslados, etc.)
  sincronizarProductosAfectados: async (productosIds, motivo, usuario) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/sincronizarProductosAfectados`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productosIds, motivo, usuario })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error sincronizando productos afectados:', error);
      throw error;
    }
  },

  // Actualizar tracking en Shopify cuando un pedido cambia a ENVIADO
  actualizarTrackingShopify: async (pedidoId, numeroGuia, urlSeguimiento, empresaTransporte, usuario, usuarioNombre) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarTrackingShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pedidoId,
          numeroGuia,
          urlSeguimiento: urlSeguimiento || null,
          empresaTransporte: empresaTransporte || null,
          usuario,
          usuarioNombre
        })
      });
      const result = await response.json();
      return result; // No lanzar error si falla, solo retornar resultado
    } catch (error) {
      console.error('Error actualizando tracking en Shopify:', error);
      return { success: false, error: error.message };
       }
  },

  // Conectar la Página de Facebook + Instagram de una marca (escaneo de seguridad del 26/10/2026, F13): el servidor
  // arma el enlace de Facebook solo para quien puede editar la configuración del chat. Devuelve el enlace.
  iniciarConexionFacebook: async (empresaId) => {
    const response = await fetchConToken(`${FUNCTIONS_URL}/chatbotFacebookOAuthInicio`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ empresaId })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success || !result.url) {
      throw new Error(result.error || `No se pudo iniciar la conexión (${response.status})`);
    }
    return result.url;
  },

  // Re-registrar webhooks de una tienda Shopify
  registrarWebhooksShopify: async (tiendaId) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/registrarWebhooksManual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error registrando webhooks:', error);
      throw error;
    }
  },

  // Importar órdenes de Shopify con rango de fechas
  importarOrdenesShopify: async (tiendaId, fechaInicio, fechaFin) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/importarOrdenesShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, fechaInicio, fechaFin })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error importando órdenes:', error);
      throw error;
    }
  },

  // Backfill de datos de origen (UTM) para auditoría de procedencia (solo admin)
  // dryRun: true previsualiza sin escribir. fechaDesde: 'YYYY-MM-DD'.
  backfillUtmShopify: async ({ fechaDesde = '2026-05-01', dryRun = false, tiendas = null } = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/backfillUtmShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fechaDesde, dryRun, tiendas })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error en backfillUtmShopify:', error);
      throw error;
    }
  },

  // Auditoría de origen: KPIs por red social sobre un rango de fechas (lee solo Firestore)
  auditarOrigenShopify: async ({ desde, hasta, tiendas = null, inferirProducto = false } = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/auditarOrigenShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ desde, hasta, tiendas, inferirProducto })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error en auditarOrigenShopify:', error);
      throw error;
    }
  },

  // Análisis CPA/ROAS por campaña (pestañas Meta / TikTok de la auditoría).
  // Cruza pedidos Shopify por campaña con el gasto real de la API de la plataforma.
  analizarCpaRoasShopify: async ({ plataforma, desde, hasta, tiendas = null, inferirProducto = false } = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/analizarCpaRoasShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plataforma, desde, hasta, tiendas, inferirProducto })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error en analizarCpaRoasShopify:', error);
      throw error;
    }
  },

  // Resolver nombres/IDs de campañas (Meta ID→nombre, TikTok nombre→ID) — ayuda visual
  resolverCampanasShopify: async ({ metaIds = [], tiktokNames = [] } = {}) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/resolverCampanasShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metaIds, tiktokNames })
      });
      const result = await response.json();
      // No tiramos error: es ayuda visual; si falla, devolvemos mapas vacíos.
      return { metaIdToName: result.metaIdToName || {}, tiktokResol: result.tiktokResol || {}, errores: result.errores || [] };
    } catch (error) {
      console.error('Error en resolverCampanasShopify:', error);
      return { metaIdToName: {}, tiktokResol: {}, errores: [String(error.message || error)] };
    }
  },

  // Obtener Borradores (Draft Orders) de Shopify
  obtenerBorradoresShopify: async (tiendaId, fechaInicio, fechaFin) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerBorradoresShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, fechaInicio, fechaFin })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error obteniendo borradores:', error);
      throw error;
    }
  },

  // Obtener Abandonados (Checkouts) de Shopify
  obtenerAbandonadosShopify: async (tiendaId) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerAbandonadosShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error obteniendo abandonados:', error);
      throw error;
    }
  },

  // Completar borrador después de crear pedido en el sistema
  completarBorradorShopify: async (tiendaId, draftOrderId, numeroPedidoSistema, usuarioNombre, clienteData) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/completarBorradorShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, draftOrderId, numeroPedidoSistema, usuarioNombre, clienteData })
      });
      const result = await response.json();
      if (!result.success) {
        const err = new Error(result.error);
        // El candado de la orden directa (07/10/2026): el borrador ya era una orden (`yaEsOrden`) o el cliente la estaba
        // terminando (`reintentar`). La pantalla arma un aviso propio para esos dos casos.
        err.yaEsOrden = !!result.yaEsOrden;
        err.reintentar = !!result.reintentar;
        err.shopifyOrderNumber = result.shopifyOrderNumber || null;
        err.borradorBorrado = !!result.borradorBorrado;
        throw err;
      }
      return result;
    } catch (error) {
      console.error('Error completando borrador:', error);
      throw error;
    }
  },

  // Crear en Shopify la orden de un carrito abandonado recuperado (mismo motor que los
  // borradores: completa, marca preparada y deja el pedido vinculado a la orden real)
  completarAbandonadoShopify: async (tiendaId, checkoutToken, numeroPedidoSistema, usuarioNombre, clienteData) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/completarAbandonadoShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, checkoutToken, numeroPedidoSistema, usuarioNombre, clienteData })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error completando carrito abandonado:', error);
      throw error;
    }
  },

  // Marcar abandonado como recuperado en Firestore
  marcarAbandonadoRecuperado: async (checkoutToken, numeroPedidoSistema, usuarioEmail, usuarioNombre) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/marcarAbandonadoRecuperado`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkoutToken, numeroPedidoSistema, usuarioEmail, usuarioNombre })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error marcando abandonado:', error);
      throw error;
    }
  },

  // Actualizar seguimiento de borrador o abandonado
  actualizarSeguimientoBorrador: async (tipoItem, itemId, campo, datos) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarSeguimientoBorrador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipoItem, itemId, campo, datos })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error actualizando seguimiento:', error);
      throw error;
    }
  },

  // Correo de último contacto al cliente ("no logramos comunicarnos").
  // Sirve para los 3 tipos: 'orden' (Pedidos Shopify), 'borrador' y 'abandonado'.
  // El destinatario y la casilla que envía los resuelve la CF (la orden se lee de
  // Firestore; el borrador/abandonado se piden a Shopify) — acá solo se dice CUÁL
  // registro. `tiendaId` es obligatorio salvo para 'orden'. Ver functions/correos.js.
  enviarCorreoCliente: async (tipo, itemId, tiendaId = null, forzarReenvio = false) => {
    const response = await fetchConToken(`${FUNCTIONS_URL}/enviarCorreoCliente`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tipo, itemId, tiendaId, forzarReenvio })
    });
    const result = await response.json().catch(() => ({}));
    if (!result.success) throw new Error(result.error || 'No se pudo enviar el correo');
    return result;
  },

  // Actualizar notas y/o observaciones de borrador o abandonado
  actualizarNotasYObservacionesBorrador: async (tipoItem, itemId, notas, observaciones, usuarioEmail, usuarioNombre, tiendaId) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/actualizarNotasYObservacionesBorrador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipoItem, itemId, notas, observaciones, usuarioEmail, usuarioNombre, tiendaId })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error actualizando notas/observaciones:', error);
      throw error;
    }
  },

  // Cancelar borrador en Shopify (agrega tag cancelado_appsis, sigue OPEN)
  // `motivoInfo` = { codigo, detalle } de la lista fija (21/10/2026); el servidor arma el texto con el código.
  cancelarBorradorShopify: async (tiendaId, draftOrderId, usuarioEmail, usuarioNombre, motivo, motivoInfo = null) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/cancelarBorradorShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, draftOrderId, usuarioEmail, usuarioNombre, motivo, motivoCodigo: motivoInfo?.codigo || null, motivoDetalle: motivoInfo?.detalle || null })
      });
      const result = await response.json();
      if (!result.success) {
        // `codigo: 'YA_TIENE_PEDIDO'` = otra persona ya lo convirtió en pedido (la cancelación en lote lo separa).
        const err = new Error(result.error);
        err.codigo = result.codigo || null;
        err.referencia = result.referencia || null;
        throw err;
      }
      return result;
    } catch (error) {
      console.error('Error cancelando borrador:', error);
      throw error;
    }
  },

  // Cancelar abandonado (solo Firestore, sin Shopify)
  cancelarAbandonadoAppsis: async (checkoutToken, usuarioEmail, usuarioNombre, motivo, notaExistente, motivoInfo = null) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/cancelarAbandonadoAppsis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkoutToken, usuarioEmail, usuarioNombre, motivo, notaExistente, motivoCodigo: motivoInfo?.codigo || null, motivoDetalle: motivoInfo?.detalle || null })
      });
      const result = await response.json();
      if (!result.success) {
        // `codigo: 'YA_TIENE_PEDIDO'` = otra persona ya lo recuperó (la cancelación en lote lo separa).
        const err = new Error(result.error);
        err.codigo = result.codigo || null;
        err.referencia = result.referencia || null;
        throw err;
      }
      return result;
    } catch (error) {
      console.error('Error cancelando abandonado:', error);
      throw error;
    }
  },

  // Descancelar borrador en Shopify (quita tag cancelado_appsis, revierte estado)
  descancelarBorradorShopify: async (tiendaId, draftOrderId, usuarioEmail, usuarioNombre, motivo) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/descancelarBorradorShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, draftOrderId, usuarioEmail, usuarioNombre, motivo })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error descancelando borrador:', error);
      throw error;
    }
  },

  // Descancelar abandonado (solo Firestore, sin Shopify)
  descancelarAbandonadoAppsis: async (checkoutToken, usuarioEmail, usuarioNombre, motivo, notaExistente) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/descancelarAbandonadoAppsis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkoutToken, usuarioEmail, usuarioNombre, motivo, notaExistente })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result;
    } catch (error) {
      console.error('Error descancelando abandonado:', error);
      throw error;
    }
  },

  // Seguimiento de Importaciones → "Calcular" (14/10/2026): lo vendido de esos productos desde el corte, de BigQuery.
  // Devuelve [{ productoId, ms, cantidad, precioVenta }] (ms = creación del pedido). La CF responde en ~6 s; 90 s de espera.
  ventasSeguimientoImportaciones: async (productoIds, corte) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/seguimientoVentasImportacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productoIds, corte }),
        signal: controller.signal
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'No se pudieron leer las ventas');
      return result.renglones;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Leer las ventas tardó demasiado y se canceló. Probá de nuevo.');
      }
      console.error('Error leyendo las ventas del seguimiento de importaciones:', error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  // Pedidos de Importación → "Ganancia probable" (16/10/2026): lo vendido de esos productos en los últimos 90 días, sin
  // las redes internas. Devuelve { desde, dias, precios: [{ productoId, cantidad, valor }] }. 90 s de espera.
  preciosVentaImportacion: async (productoIds) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/importacionPreciosVenta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productoIds }),
        signal: controller.signal
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'No se pudieron leer las ventas');
      return { desde: result.desde, dias: result.dias, precios: result.precios || [] };
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Leer las ventas tardó demasiado y se canceló. Probá de nuevo.');
      }
      console.error('Error leyendo los precios de venta del pedido de importación:', error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  // Análisis de importaciones con IA (DeepSeek, del lado servidor). Manda la data
  // ya calculada de un producto-grupo + el historial de chat + los días que tiene que
  // alcanzar (14/10/2026: "Cuánto traer"); devuelve la respuesta y la cantidad elegida.
  analizarImportacionIA: async (producto, mensajes = [], dias) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 125000); // 125s (la CF corta a 120s)
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/analizarImportacionIA`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ producto, mensajes, dias }),
        signal: controller.signal
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Error en el análisis con IA');
      return result; // { respuesta, cantidad, cantidadIA, dias, tokensInput, tokensOutput }
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('El análisis tardó demasiado y se canceló. Probá de nuevo.');
      }
      console.error('Error en análisis de importación con IA:', error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  // ── Productos 3D (14/10/2026, docs/planes/PRODUCTOS_3D_PLAN.md) ──
  // Las 3 funciones del módulo: el panel (solo lee), el análisis con IA (+ chat) y TODAS las escrituras. El navegador
  // no escribe esas colecciones (firestore.rules): todo pasa por `productos3dGuardar`, que valida y registra la actividad.
  productos3dPanel: async (forzar = false) => llamarProductos3D('productos3dPanel', { forzar }, 70000),
  // La función tiene 180 s (datos + IA con corte propio de 140 s): el navegador espera un poco más.
  // `ajustes` = { [productoId]: { hay, enCamino } }: lo que se escribió al analizar (vale solo para ese análisis).
  productos3dAnalizar: async ({ productoIds, diasCubrir, diasEntrega, ajustes }) =>
    llamarProductos3D('productos3dAnalizar', { accion: 'analizar', productoIds, diasCubrir, diasEntrega, ajustes: ajustes || {} }, 185000),
  productos3dChat: async (analisisId, mensaje) =>
    llamarProductos3D('productos3dAnalizar', { accion: 'chat', analisisId, mensaje }, 185000),
  // "¿Acertó?" de un análisis guardado (2ª etapa, 14/10/2026): solo lee.
  productos3dAcierto: async (analisisId) =>
    llamarProductos3D('productos3dAnalizar', { accion: 'acierto', analisisId }, 70000),
  productos3dGuardar: async (accion, datos = {}) =>
    llamarProductos3D('productos3dGuardar', { ...datos, accion }, 70000),

  // ── Conciliación Bancaria (14/10/2026, docs/planes/CONCILIACION_BANCARIA_PLAN.md) ──
  // `conciliacionGuardar`: { mes, archivos: [{ cuenta, titular, desde, hasta, huella, candadoOk, movimientos, lineas }],
  // motivoReemplazo? } — SOLO las líneas que pueden ser de Forza; el archivo nunca sale de la computadora.
  conciliacionGuardar: async (payload) => llamarConciliacion('conciliacionGuardar', payload),
  // contexto (solo lee) · recalcular · confirmar · quitarConfirmacion · anotar · cerrar · reabrir · anularArchivo
  conciliacionAccion: async (accion, datos = {}) => llamarConciliacion('conciliacionAccion', { ...datos, accion }),

  // ── Tiempos de Producción (21/10/2026, docs/planes/TIEMPOS_PRODUCCION_PLAN.md) ──
  // guardarConfig · crearListaInicial · guardarTipo · cambiarEstadoTipo · asignarTipos · registrarMedicion ·
  // anularMedicion. Mismo ayudante que Conciliación (token + corte de 130 s): la función responde { success, ... }.
  produccionAccion: async (accion, datos = {}) => llamarConciliacion('produccionAccion', { ...datos, accion }),

  // Obtener pedidos existentes de un cliente en Shopify
  obtenerPedidosClienteShopify: async (tiendaId, customerId) => {
    try {
      const response = await fetchConToken(`${FUNCTIONS_URL}/obtenerPedidosClienteShopify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiendaId, customerId })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch (error) {
      console.error('Error obteniendo pedidos del cliente:', error);
      throw error;
    }
  }

};
